/*
  map.js - shared Google Maps settings (TB-063).

  Every map in the app opens on Bahrain and cannot be panned far outside it.
  The Maps key comes from the build env (VUE_APP_GOOGLE_MAPS_KEY) and is never
  committed. Coordinates are plain numbers in degrees.
*/

export const BAHRAIN_CENTER = Object.freeze({ lat: 26.0667, lng: 50.5577 })
export const DEFAULT_ZOOM = 11
export const MIN_ZOOM = 9

// Approximate box around the kingdom, Hawar islands included.
// Symmetric around BAHRAIN_CENTER: when the viewport is wider or taller than the
// box, Google centres the map on the box midpoint, which must be the centre.
export const BAHRAIN_BOUNDS = Object.freeze({
  north: 26.5834,
  south: 25.55,
  east: 50.8577,
  west: 50.2577
})

// strictBounds false: the map may show a little sea around the box, but the
// centre cannot leave it.
export const MAP_RESTRICTION = Object.freeze({
  latLngBounds: BAHRAIN_BOUNDS,
  strictBounds: false
})

export const MAPS_KEY = process.env.VUE_APP_GOOGLE_MAPS_KEY
export const MAPS_REGION = 'BH'
export const MAPS_COUNTRY = 'BH'

// Google labels follow the page language; the app ships English and Arabic.
export function mapsLanguage () {
  try {
    const lang = (document.documentElement.lang || navigator.language || 'en').toLowerCase()
    return lang.startsWith('ar') ? 'ar' : 'en'
  } catch (e) {
    return 'en'
  }
}

// A real coordinate pair: finite numbers in range and not the 0,0 placeholder.
export function isValidCoord (lat, lng) {
  const la = Number(lat)
  const ln = Number(lng)
  if (lat === null || lng === null || lat === '' || lng === '') return false
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false
  if (la < -90 || la > 90 || ln < -180 || ln > 180) return false
  return !(la === 0 && ln === 0)
}

export function inBahrain (lat, lng) {
  if (!isValidCoord(lat, lng)) return false
  const la = Number(lat)
  const ln = Number(lng)
  return la >= BAHRAIN_BOUNDS.south && la <= BAHRAIN_BOUNDS.north &&
    ln >= BAHRAIN_BOUNDS.west && ln <= BAHRAIN_BOUNDS.east
}

// Base GMapMap options shared by every map; callers add styles for the theme.
export const BASE_MAP_OPTIONS = Object.freeze({
  restriction: MAP_RESTRICTION,
  minZoom: MIN_ZOOM,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false
})

// "lat,lng" with 6 decimals, used by data-* attributes for the e2e suite.
export function formatCoord (lat, lng) {
  if (!isValidCoord(lat, lng)) return ''
  return `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`
}

// City and country long names from Google address_components (TB-130).
export function placeParts (components) {
  const list = Array.isArray(components) ? components : []
  const pick = (type) => {
    const c = list.find((item) => item && item.types && item.types.includes(type))
    return c ? c.long_name : ''
  }
  return {
    city: pick('locality') || pick('postal_town') || pick('sublocality') ||
      pick('administrative_area_level_2') || pick('administrative_area_level_1'),
    country: pick('country')
  }
}
